import { doc, getDoc, updateDoc, collection, query, where, getDocs } from "firebase/firestore";
import { db } from "../firebase";

export async function getUser(uid) {
  const snap = await getDoc(doc(db, "users", uid));
  return snap.exists() ? snap.data() : null;
}

export async function saveDesignerProfile(uid, profile, opts = {}) {
  const patch = {
    designerProfile: profile,
    profileComplete: true,
    updatedAt: Date.now(),
  };
  // Top-level opt-in for "new case" email alerts (default ON when undefined).
  if (opts.newCaseEmails !== undefined) patch.newCaseEmails = !!opts.newCaseEmails;
  await updateDoc(doc(db, "users", uid), patch);
}

// Records the designer's acceptance of the manual-payout policy (versioned).
export async function acceptPayoutPolicy(uid, policyVersion) {
  await updateDoc(doc(db, "users", uid), {
    payoutPolicy: { accepted: true, policyVersion, acceptedAt: Date.now() },
    updatedAt: Date.now(),
  });
}

// Lab/clinic organization & contact info (editable after registration). Optional
// credibility fields (website, description, yearsInBusiness, services) are additive.
export async function updateOrgProfile(uid, data) {
  const clean = {};
  ["name", "accountType", "accountTypeOther", "contactName", "phone", "website", "description", "yearsInBusiness", "services"].forEach((k) => {
    if (data[k] !== undefined) clean[k] = data[k];
  });
  if (data.address) {
    clean.address = {
      street: data.address.street || "",
      city: data.address.city || "",
      state: data.address.state || "",
      postalCode: data.address.postalCode || "",
      country: data.address.country || "",
    };
  }
  clean.updatedAt = Date.now();
  // Recovered / incomplete accounts become "complete" once the essential
  // organization + contact fields are filled, which hides the completion banner.
  if (clean.name && clean.accountType && clean.phone && clean.address?.country) {
    clean.profileComplete = true;
    clean.recovered = false;
  }
  await updateDoc(doc(db, "users", uid), clean);
}

// Lab/clinic default dental numbering system ("fdi" | "universal"), used as the
// default for new cases. Can still be overridden per case.
export async function setNumberingPreference(uid, numbering) {
  await updateDoc(doc(db, "users", uid), {
    numberingPreference: numbering === "universal" ? "universal" : "fdi",
    updatedAt: Date.now(),
  });
}

// Returns the uid that already owns this account code, or null if it is free.
export async function accountCodeOwner(code) {
  const q = query(collection(db, "users"), where("accountCode", "==", code));
  const snap = await getDocs(q);
  return snap.empty ? null : snap.docs[0].id;
}

// Sets a unique, uppercase, alphanumeric account code for a lab. Throws "taken" if in use.
export async function setAccountCode(uid, code) {
  const owner = await accountCodeOwner(code);
  if (owner && owner !== uid) {
    const err = new Error("taken");
    err.code = "taken";
    throw err;
  }
  await updateDoc(doc(db, "users", uid), { accountCode: code, updatedAt: Date.now() });
}

function baseCodeFromName(name) {
  const words = (name || "").toUpperCase().replace(/[^A-Z0-9 ]/g, "").split(/\s+/).filter(Boolean);
  const clean = (name || "").toUpperCase().replace(/[^A-Z0-9]/g, "");
  let base = words.length >= 2 ? words.map((w) => w[0]).join("").slice(0, 4) : clean.slice(0, 4);
  if (base.length < 2) base = (clean + "LAB").slice(0, 3);
  return base || "LAB";
}

// Auto-generates a short, unique account code from the lab's name. Never blocks:
// if the base is taken, appends a numeric (then random) suffix until it's free.
export async function ensureAccountCode(uid, name) {
  const me = await getUser(uid);
  if (me?.accountCode) return me.accountCode;
  const base = baseCodeFromName(name);
  let code = base;
  let n = 1;
  // eslint-disable-next-line no-await-in-loop
  while (await accountCodeOwner(code)) {
    n += 1;
    code = `${base}${n}`;
    if (n > 30) {
      code = `${base}${Math.random().toString(36).slice(2, 5).toUpperCase()}`;
      break;
    }
  }
  await updateDoc(doc(db, "users", uid), { accountCode: code, updatedAt: Date.now() });
  return code;
}
